//@ts-nocheck

import React, { useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Typography,
    Box,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useSetSnackBar } from '../../store/Snackbar.state';
import { useGetMerchantUserPortfolioList, useAssignMultipleMerchantToPortfolio } from '../../hooks';
import { Loading } from './Loading';

export const MerchantBulkPortfolioModal = ({ open, setOpen, selectedRows, setSelectedRows, currentRequest }) => {
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const setSnackbar = useSetSnackBar();
    const { data: rawUserPortfolioList } = useGetMerchantUserPortfolioList();
    const { mutate: assignMultipleMerchantToPortfolio, isLoading: isAssignLoading } = useAssignMultipleMerchantToPortfolio();

    const userPortfolioList = useMemo(() => {
        return rawUserPortfolioList?.data?.map((user: { id: number; fullName: string; mail: string }) => {
            return { label: `${user.fullName} (${user.mail})`, value: `${user.id}`, fullName: user.fullName, mail: user.mail };
        }) || [];
    }, [rawUserPortfolioList?.data]);

    const filteredUserPortfolioList = useMemo(() => {
        if (!searchTerm) return userPortfolioList;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return userPortfolioList.filter((user) =>
            user.fullName.toLowerCase().includes(lowerSearchTerm) ||
            user.mail.toLowerCase().includes(lowerSearchTerm)
        );
    }, [userPortfolioList, searchTerm]);

    const handleClose = () => {
        setOpen(false);
        setSelectedUser(null);
        setSearchTerm('');
    };

    const handleSelectUser = (value: string) => {
        setSelectedUser(value);
    };

    const handleConfirm = () => {
        if (selectedUser) {
            let request = {
                userId: selectedUser,
                merchantId: selectedRows.map(x => x.merchantId),
                currentRequest: currentRequest
            };

            assignMultipleMerchantToPortfolio(request, {
                onSuccess: (data) => {
                    if (data.isSuccess) {
                        setSnackbar({
                            severity: 'success',
                            description: 'İşlem Başarılı',
                            isOpen: true,
                        });
                        setSelectedRows();
                        handleClose();
                    } else {
                        setSnackbar({
                            severity: 'error',
                            description: data.message,
                            isOpen: true,
                        });
                    }
                },
                onError: () => {
                    setSnackbar({
                        severity: 'error',
                        description: 'İşlem sırasında bir hata oluştu',
                        isOpen: true,
                    });
                },
            });
        }
    };

    return (
        <>
            {isAssignLoading && <Loading />}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Portföy Atama</DialogTitle>
                <DialogContent>
                    {/* Redesigned Search Input */}
                    <Box
                        sx={{
                            mb: 2,
                            px: 1, // Align with list padding
                            backgroundColor: 'grey.50', // Subtle background
                            borderRadius: '8px', // Rounded corners
                            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)', // Light shadow
                            display: 'flex',
                            alignItems: 'center',
                            py: 0.5, // Compact height
                        }}
                    >
                        <TextField
                            fullWidth
                            variant="standard" // Minimal underline style
                            placeholder="Ad veya e-posta ile ara"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                disableUnderline: true, // Remove default underline
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'grey.500' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchTerm('')}
                                            edge="end"
                                        >
                                            <ClearIcon sx={{ color: 'grey.500' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    py: 1, // Adjust padding for compactness
                                    fontSize: '0.95rem', // Slightly smaller text
                                },
                            }}
                        />
                    </Box>

                    {filteredUserPortfolioList.length > 0 ? (
                        <Box
                            sx={{
                                maxHeight: '250px',
                                overflowY: 'auto',
                                scrollBehavior: 'smooth',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.4)',
                                },
                            }}
                        >
                            <List>
                                {filteredUserPortfolioList.map((user) => (
                                    <ListItem
                                        key={user.value}
                                        disablePadding
                                        sx={{
                                            my: 0.5,
                                            borderRadius: '8px',
                                            backgroundColor: selectedUser === user.value ? 'primary.light' : 'white',
                                            boxShadow: selectedUser === user.value
                                                ? '0px 4px 12px rgba(0, 0, 0, 0.15), 0px 1px 3px rgba(0, 0, 0, 0.1)'
                                                : '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: 'grey.100',
                                                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => handleSelectUser(user.value)}
                                            sx={{
                                                py: 2,
                                                px: 3,
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    mr: 2,
                                                    bgcolor: selectedUser === user.value ? 'primary.main' : 'grey.400',
                                                }}
                                            >
                                                <PersonIcon />
                                            </Avatar>
                                            <ListItemText
                                                primary={user.fullName}
                                                secondary={user.mail}
                                                primaryTypographyProps={{
                                                    fontWeight: 'medium',
                                                    color: selectedUser === user.value ? 'primary.dark' : 'text.primary',
                                                }}
                                                secondaryTypographyProps={{
                                                    color: selectedUser === user.value ? 'primary.main' : 'text.secondary',
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ) : (
                        <Typography>
                            {searchTerm ? 'Aramanıza uygun kullanıcı bulunamadı.' : 'Portföy kullanıcı bulunamadı.'}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        İptal
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        color="primary"
                        variant="contained"
                        disabled={!selectedUser}
                    >
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};